/**
 * Cohort detail: the editable fields, the members, and the danger zone.
 *
 * Three things an operator needs before a wave goes out, and this page is
 * where each is visible:
 *
 *   1. **Who is in it**, with the start date locked onto their enrollment.
 *   2. **Whose classification disagrees** with the cohort's current
 *      default. That happens legitimately (a member keeps what they were
 *      created with) and it is surfaced
 *      rather than quietly reconciled, because the failure runs both ways:
 *      a tester counted as a customer, or a customer dropped from the
 *      numbers.
 *
 * English-only by design: internal tooling outside lesson-content review.
 */
import { SectionHeader } from '../common/section-header'
import { PageHeader } from '../components/page-header'
import { OPERATOR_DETAIL_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  buttonVariants,
  FieldHelp,
  Icon,
  Notice,
  useToast,
} from '@speaking-coach/ui'
import {
  AccessWindowPanel,
  formatBusinessDate as formatWindowDate,
  formatWindowLength,
  todayJstIso,
} from '../common/access-window'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { keysStaleAfterCohortChange, queryKeys } from '../api/query-keys'
import { adminClientsApi } from '../clients/api'
import { SaveButton } from '../common/save-button'
import { UnsavedChangesGuard } from '../common/unsaved-changes'
import { PREVIEW_CELL, PreviewNameLink, PreviewTable } from '../common/preview-table'
import { TABLE_ROW_NESTED } from '../common/table'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { adminCohortClient, messageForCohortError } from './api'
import {
  AccessEndsField,
  CohortClassificationFields,
  CohortDescriptionField,
  EnrollmentDefaultsFields,
  CohortInternalField,
  CohortTrialField,
  CohortNameField,
  InternalBadge,
  ProductTierField,
  SendScheduleFields,
  StartDateField,
} from './cohort-form-fields'
import {
  sendScheduleFromCohort,
  sendScheduleToBody,
} from './cohort-send-schedule'
import { cohortSchema, type CohortForm } from './cohort-schema'
import type { CohortDetail } from './types'
import { CohortHrAccessSection } from './cohort-hr-access-section'
import { HistoryLink } from '../activity/history-link'
import { CohortDetailFunnel } from './cohort-funnel'
import { HighConsequenceActionPanel } from '../common/high-consequence-action-panel'
import { OperatorFaqTrigger } from '../operator-faq/operator-faq'

export function CohortDetailPage(): JSX.Element {
  const { clientId = '', cohortId = '' } = useParams<{
    clientId: string
    cohortId: string
  }>()
  const cohortQuery = useQuery({
    queryKey: queryKeys.cohort(clientId, cohortId),
    queryFn: () => adminCohortClient.get(clientId, cohortId),
  })

  const cohort = cohortQuery.data ?? null
  const loadError =
    cohortQuery.error !== null
      ? messageForCohortError(cohortQuery.error)
      : null

  if (loadError !== null) {

    return (
      <main className={OPERATOR_PAGE_MAIN}>
        <div className={OPERATOR_DETAIL_PAGE}>
          <Notice variant="danger" className="mt-4 bg-surface-card">
            {loadError}
          </Notice>
        </div>
      </main>
    )
  }

  if (cohort === null) {
    return (
      <main className={OPERATOR_PAGE_MAIN}>
        <p className="text-text-secondary">Loading cohort…</p>
      </main>
    )
  }

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_DETAIL_PAGE}>
        <PageHeader
          title={cohort.name}
          meta={
            <>
              {cohort.isTrial && <Badge variant="muted">Trial</Badge>}
              {cohort.resolvedIsInternal && <InternalBadge />}
            </>
          }
          action={
            <div className="flex items-center gap-2">
              <Link className={buttonVariants({variant:'secondary',size:'sm'})} to={`/admin/clients/${clientId}/cohorts/${cohortId}/analytics`}>
                <Icon name="analytics" size={14}/>Analytics
              </Link>
              <HistoryLink to={`/admin/clients/${clientId}/cohorts/${cohortId}/history`}/>
            </div>
          }
        />

        <section
          aria-label="Cohort funnel"
          className="mb-4 bg-surface-card rounded-token-card p-4"
        >
          <SectionHeader
            title="Student funnel"
            meta={<FieldHelp label="cohort student funnel">
              Each number includes everyone who reached that stage. Some older
              students may not have a tracked invitation. Use the Attention
              links to see who needs action and why.
            </FieldHelp>}
            className="mb-2"
          />
          <CohortDetailFunnel cohort={cohort} />
          <p className="mt-2 text-caption text-text-secondary">Started learning uses the existing session status. Confirmed practice requires a regular lesson with recorded student speech. Activated is an account-access status, not an invitation-redemption rate.</p>
        </section>

        <EditSection cohort={cohort} />
        <MembersSection cohort={cohort} clientId={clientId} />
        <CohortHrAccessSection cohort={cohort} clientId={clientId} />
        <DangerSection cohort={cohort} clientId={clientId} />
      </div>
    </main>
  )
}

function EditSection({ cohort }: { cohort: CohortDetail }): JSX.Element {
  /** The same schema as the create page — a start date is always required. */
  const form = useForm<CohortForm>({
    resolver: zodResolver(cohortSchema()),
    mode: 'onSubmit',
    defaultValues: {
      name: cohort.name,
      description: cohort.description ?? '',
      scheduledStartOn: cohort.scheduledStartOn ?? '',
      accessEndsOn: cohort.accessEndsOn,
      defaultTier: cohort.defaultTier,
      defaultProgramId: cohort.defaultProgramId,
      defaultTrophyGoal:
        cohort.defaultTrophyGoal === null
          ? ''
          : String(cohort.defaultTrophyGoal),
      externalPolicyRef: cohort.externalPolicyRef ?? '',
      isTrial: cohort.isTrial,
      isInternal: cohort.isInternal,
      hrUserIds: [],
      ...(() => {
        const initial = sendScheduleFromCohort(cohort)
        return {
          sendMode: initial.mode,
          sendFixedAt: initial.fixedAt,
          sendOffsetDays: initial.offsetDays,
        }
      })(),
    },
  })
  const { control, handleSubmit, setValue, watch, reset, formState } = form
  const errors = formState.errors
  const scheduledStartOn = watch('scheduledStartOn')
  const accessEndsOn = watch('accessEndsOn')
  const sendMode = watch('sendMode')

  // Only so the Classification field can say which way "Same as the client"
  // resolves. Already in cache whenever the operator arrived via the client
  // page, which is the normal route in.
  const clientQuery = useQuery({
    queryKey: queryKeys.client(cohort.clientId),
    queryFn: () => adminClientsApi.get(cohort.clientId),
  })
  const queryClient = useQueryClient()

  // Clearing the start date invalidates a relative send: the server refuses the
  // pair (checked against the MERGED row, not just this request), so drop back
  // rather than submit a 422 for a field the operator can no longer see.
  const clearRelativeSendWithoutStart = useCallback(
    (nextStart: string) => {
      if (nextStart !== '' || sendMode !== 'offset') return
      // `shouldDirty`: this changes what a save would send, so it must count
      // toward the dirty state that gates Save and arms the guard.
      setValue('sendMode', 'none', { shouldDirty: true })
      setValue('sendFixedAt', '', { shouldDirty: true })
    },
    [setValue, sendMode],
  )

  const save = useMutation({
    mutationFn: (values: CohortForm) =>
      adminCohortClient.update(cohort.clientId, cohort.cohortId, {
        name: values.name.trim(),
        description:
          (values.description ?? '').trim() === ''
            ? null
            : (values.description ?? '').trim(),
        // Never null: the schema requires a start date, so an emptied field
        // fails validation rather than reaching here. Sending null was how the
        // edit form could manufacture a cohort with no start — the state the
        // create route forbids.
        scheduledStartOn: values.scheduledStartOn,
        accessEndsOn: values.accessEndsOn,
        defaultTier: values.defaultTier,
        defaultProgramId: values.defaultProgramId.trim(),
        defaultTrophyGoal: Number(values.defaultTrophyGoal),
        externalPolicyRef: values.externalPolicyRef.trim() || null,
        isTrial: values.isTrial,
        isInternal: values.isInternal,
        // Always both, always together — the server treats them as a
        // pair, which is what makes fixed→offset a single save.
        ...sendScheduleToBody({
          mode: values.sendMode,
          fixedAt: values.sendFixedAt,
          offsetDays: values.sendOffsetDays,
        }),
      }),
    onSuccess: (next) => {
      // Re-seed from the SERVER's row, so the form reflects what was stored
      // rather than what was typed — the two differ whenever the server
      // normalizes (a cleared start date comes back as null).
      const nextSend = sendScheduleFromCohort(next)
      reset({
        name: next.name,
        description: next.description ?? '',
        scheduledStartOn: next.scheduledStartOn ?? '',
        accessEndsOn: next.accessEndsOn,
        defaultTier: next.defaultTier,
        defaultProgramId: next.defaultProgramId,
        defaultTrophyGoal:
          next.defaultTrophyGoal === null
            ? ''
            : String(next.defaultTrophyGoal),
        externalPolicyRef: next.externalPolicyRef ?? '',
        isTrial: next.isTrial,
        isInternal: next.isInternal,
        hrUserIds: [],
        sendMode: nextSend.mode,
        sendFixedAt: nextSend.fixedAt,
        sendOffsetDays: nextSend.offsetDays,
      })
      queryClient.setQueryData(
        queryKeys.cohort(cohort.clientId, cohort.cohortId),
        next,
      )
      // The client page's cohort table, the global cohort list and the
      // students page (which renders a cohort name) are all stale now. Naming all
      // three here is the whole point of ADR-0056: before this, whichever
      // page you were NOT looking at kept showing the old row.
      for (const key of keysStaleAfterCohortChange(cohort.clientId)) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  const saving = save.isPending
  const saved = save.isSuccess
  const error = save.error !== null ? messageForCohortError(save.error) : null

  return (
    <section aria-label="Cohort settings" className="bg-surface-card rounded-token-card p-6">
      <UnsavedChangesGuard
        when={formState.isDirty}
        name={`cohort ${cohort.name}`}
      />
      {/* `noValidate`: the schema is the authority, and native constraint
          validation would pre-empt it with a browser bubble. */}
      <form
        onSubmit={handleSubmit((values) => {
          // Disabled buttons do not stop Enter in a text field.
          if (!formState.isDirty || save.isPending) return
          save.mutate(values)
        })}
        className="space-y-4"
        noValidate
      >
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <CohortNameField
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.name?.message ?? null}
                />
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <CohortDescriptionField
              value={field.value ?? ''}
              onChange={field.onChange}
            />
          )}
        />
        {/* The shared panel — same shape as the create form and the student
            pages. This page used to render the two fields bare, which was
            the third variant of one concept. */}
        <AccessWindowPanel
          titleId="cohort-edit-program-access-window-title"
          windowLength={formatWindowLength(scheduledStartOn, accessEndsOn)}
          badge="Defaults for future enrollments"
          footer={
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              {cohort.memberCount > 0 ? (
                <p className="text-caption text-text-secondary">
                  Existing enrollment periods keep their locked dates. Changes
                  here apply only to enrollments created after you save.
                </p>
              ) : null}
              <OperatorFaqTrigger
                topicId="cohort-enrollment-defaults"
                label="Open guide"
                className="ml-auto shrink-0"
              />
            </div>
          }
        >
          <Controller
            control={control}
            name="scheduledStartOn"
            render={({ field }) => (
              <StartDateField
                value={field.value}
                onChange={(next) => {
                  field.onChange(next)
                  clearRelativeSendWithoutStart(next)
                }}
                error={errors.scheduledStartOn?.message ?? null}
              />
            )}
          />
          <Controller
            control={control}
            name="accessEndsOn"
            render={({ field }) => (
              <AccessEndsField
                value={field.value}
                onChange={field.onChange}
                startDate={scheduledStartOn}
                error={errors.accessEndsOn?.message ?? null}
              />
            )}
          />
        </AccessWindowPanel>
        <SendScheduleFields
          mode={sendMode}
          fixedAt={watch('sendFixedAt')}
          offsetDays={watch('sendOffsetDays')}
          hasStartDate={scheduledStartOn !== ''}
          startDate={scheduledStartOn}
          accessEndsOn={accessEndsOn}
          error={
            errors.sendFixedAt?.message ?? errors.sendOffsetDays?.message ?? null
          }
          // `shouldDirty` on all three: these are operator edits like any
          // typed field, and without it a send-schedule-only change would
          // leave Save disabled by the dirty gate.
          onModeChange={(mode) =>
            setValue('sendMode', mode, { shouldDirty: true })
          }
          onFixedChange={(fixedAt) =>
            setValue('sendFixedAt', fixedAt, { shouldDirty: true })
          }
          onOffsetChange={(offsetDays) =>
            setValue('sendOffsetDays', offsetDays, { shouldDirty: true })
          }
        />
        {cohort.warnings
          .filter((warning) => warning.scope === 'settings')
          .map((warning) => (
            <Notice
              key={warning.message}
              variant="warning"
              textSize="caption"
            >
              {warning.message}
            </Notice>
          ))}
        <Controller
          control={control}
          name="defaultTier"
          render={({ field }) => (
            <ProductTierField value={field.value} onChange={field.onChange} isEdit />
          )}
        />
        <EnrollmentDefaultsFields
          programId={watch('defaultProgramId')}
          currentProgram={cohort.defaultProgram}
          trophyGoal={watch('defaultTrophyGoal')}
          externalPolicyRef={watch('externalPolicyRef')}
          programIdError={errors.defaultProgramId?.message ?? null}
          trophyGoalError={errors.defaultTrophyGoal?.message ?? null}
          onProgramChange={(value) =>
            setValue('defaultProgramId', value, { shouldDirty: true })
          }
          onTrophyGoalChange={(value) =>
            setValue('defaultTrophyGoal', value, { shouldDirty: true })
          }
          onExternalPolicyRefChange={(value) =>
            setValue('externalPolicyRef', value, { shouldDirty: true })
          }
          isEdit
        />
        <CohortClassificationFields>
          <Controller
            control={control}
            name="isInternal"
            render={({ field }) => (
              <CohortInternalField
                value={field.value}
                onChange={field.onChange}
                clientIsInternal={clientQuery.data?.isInternal}
              />
            )}
          />
          <Controller
            control={control}
            name="isTrial"
            render={({ field }) => (
              <CohortTrialField
                value={field.value}
                onChange={field.onChange}
                locked={cohort.trialFlagLocked}
              />
            )}
          />
        </CohortClassificationFields>

        {error && (
          <Notice variant="danger">
            {error}
          </Notice>
        )}
        {/* `FormBlockers` is gone: every message has a field path now, so
            there is no leftover pile with nowhere to render. */}

        {/* The confirmation lives ON the button ("Saved ✓") rather than in
            a line that pushes the layout down; a clean form's Save is
            disabled because re-saving an unchanged row is never what an
            operator means. */}
        <SaveButton
          pending={saving}
          dirty={formState.isDirty}
          justSaved={saved}
        />
      </form>
    </section>
  )
}

function MembersSection({
  cohort,
  clientId,
}: {
  cohort: CohortDetail
  clientId: string
}): JSX.Element {
  return (
    <section aria-label="Members" className="bg-surface-card rounded-token-card p-6 mt-4">
      {/* Same header shape as the client page's Cohorts section: title
          left, the CREATE action as a primary button right. Both steps of
          the batch form arrive pre-answered — this page IS the client and
          the cohort. */}
      <SectionHeader
        title={<>Members ({cohort.memberCount})</>}
        meta={<FieldHelp label="cohort members">
            Create cohort enrollments for new or returning students here.
            Membership is locked per enrollment; to change it, end or replace
            the period from the student page.
          </FieldHelp>}
        actions={cohort.accessEndsOn < todayJstIso() ? (
          <div className="flex items-center gap-2">
            <span className="text-text-secondary text-caption">
              Ended {formatWindowDate(cohort.accessEndsOn)} — extend the
              access end to add students.
            </span>
            <Button variant="primary" size="sm" disabled>
              Enroll several students
            </Button>
          </div>
        ) : (
          <Link
            to={`/admin/students/batch?clientId=${clientId}&cohortId=${cohort.cohortId}`}
            className={buttonVariants({ variant: 'primary', size: 'sm' })}
          >
            Enroll several students
          </Link>
        )}
        className="mb-1"
      />
      {cohort.memberCount > cohort.members.length && (
        <p className="text-text-secondary text-caption mb-3">
          Showing the first {cohort.members.length} of {cohort.memberCount}.{' '}
          <Link
            to={`/admin/students?clientId=${clientId}&cohortId=${cohort.cohortId}`}
            className="underline"
          >
            View all students
          </Link>
          .
        </p>
      )}

      {cohort.members.length === 0 ? (
        <p className="text-text-secondary text-body">No members yet.</p>
      ) : (
        <>
          {/* The shared preview shell: base columns match the client
              page's Roster block; Status, Starts and Classification are the
              cohort-specific extras — they answer questions that only
              exist here (who is still invited into the wave and who
              mismatches the classification default). */}
          <PreviewTable
            headers={[
              'Name',
              'Email',
              'Tier',
              'Enrollment status',
              'Starts',
              'Classification',
            ]}
          >
            {cohort.members.map((m) => (
              <tr key={m.userId} className={TABLE_ROW_NESTED}>
                <td className={PREVIEW_CELL}>
                  <PreviewNameLink userId={m.userId}>{m.name}</PreviewNameLink>
                </td>
                <td className={`${PREVIEW_CELL} text-text-secondary`}>{m.email}</td>
                <td className={PREVIEW_CELL}>{m.tier}</td>
                <td className={`${PREVIEW_CELL} text-text-secondary`}>{m.enrollmentStatus}</td>
                <td className={PREVIEW_CELL}>
                  {m.scheduledStartOn ?? 'Not scheduled'}
                </td>
                <td className={PREVIEW_CELL}>
                  {m.isInternal ? <InternalBadge /> : 'customer'}
                  {!m.matchesCohortClassification && (
                    <span
                      className="text-text-secondary block text-table-secondary"
                      title="This student's classification differs from the cohort default. That can happen when the cohort default changes after the student identity was created."
                    >
                      <Icon name="warning" className="mr-1 inline align-text-bottom" />differs from cohort default
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </PreviewTable>
        </>
      )}
    </section>
  )
}

function DangerSection({
  cohort,
  clientId,
}: {
  cohort: CohortDetail
  clientId: string
}): JSX.Element {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const hasEnrollmentHistory = cohort.enrollmentHistoryCount > 0

  const remove = useMutation({
    mutationFn: () => adminCohortClient.remove(clientId, cohort.cohortId),
    onSuccess: () => {
      for (const key of keysStaleAfterCohortChange(clientId)) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
      // The page we are about to leave is where the confirmation would
      // normally render, so it goes to a toast that outlives the navigation
      // (ADR-0056). Before this it was simply lost.
      toast({ variant: 'success', description: `Deleted cohort ${cohort.name}.` })
      navigate(`/admin/clients/${clientId}`)
    },
  })

  const deleting = remove.isPending
  const error = remove.error !== null ? messageForCohortError(remove.error) : null

  return (
    <HighConsequenceActionPanel
      dangerZone
      title="Delete cohort"
      className="mt-4"
      description={
        hasEnrollmentHistory
          ? `Not while it has enrollment history — ${cohort.enrollmentHistoryCount} ${cohort.enrollmentHistoryCount === 1 ? 'period remains' : 'periods remain'} available for audit and reporting.`
          : 'The name becomes available again immediately. Student records are untouched.'
      }
      error={error}
    >
      {/*
        Behavior change, deliberate (ADR-0056): this button used to delete on
        a single click. Every other destructive action in the app sits behind
        a confirm step, and the Admin UI conventions require one — this was
        simply missing, and adopting the primitive is what made fixing it
        cheap rather than a third hand-rolled confirm.
      */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            disabled={deleting || hasEnrollmentHistory}
            variant="dangerOpen"
          >
            {deleting ? 'Deleting…' : 'Delete cohort'}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {cohort.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The name becomes available again immediately. This is available
              only before any enrollment period has referenced the cohort, so
              no student history or reporting scope can be hidden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => remove.mutate()}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </AlertDialogAction>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </HighConsequenceActionPanel>
  )
}
